<template>
  <Background
    :background
    class="p-24"
    container-class="!items-start"
    rounded="always"
  >
    <RevCoupon
      v-if="coupon"
      class="mb-8 mood-main"
      data-test="countdown-coupon"
      :icon="unref(fetchedIcon)"
      :label="coupon.text"
      :tooltip-text="coupon.infoLabel ?? undefined"
    />

    <div
      class="flex flex-col md:flex-row justify-between w-full gap-16 md:gap-24 flex-wrap sm:flex-nowrap"
    >
      <div>
        <RichText
          class="heading-1 md:punchline text-static-default-hi hyphens-auto"
          :node-renderers-overrides="renderParagraphAsHeading"
          :rich-text="title"
          style-variant="none"
        />

        <RichText
          v-if="subtitle"
          class="body-2 md:body-1 text-static-default-hi mt-8 md:mt-4 hyphens-auto"
          :rich-text="subtitle"
          style-variant="none"
        />
      </div>

      <ClientOnly>
        <RevCounter
          class="w-fit hidden flex-nowrap md:flex"
          data-test="desktop-timer"
          :date="new Date(saleStartDate)"
          :date-labels="desktopDateLabels"
          date-max-unit="days"
          :mood="mood ?? 'main'"
          size="large"
        />

        <RevCounter
          class="w-fit flex md:hidden flex-nowrap"
          data-test="mobile-timer"
          :date="new Date(saleStartDate)"
          :date-labels="mobileDateLabels"
          date-max-unit="days"
          :mood="mood ?? 'main'"
          size="medium"
        />

        <template #fallback>
          <RevSkeleton
            class="hidden md:block min-w-[345px]"
            height="80px"
            shape="rectangle"
            width="345px"
          />

          <RevSkeleton
            class="block md:hidden min-w-[190px]"
            height="60px"
            shape="rectangle"
            width="190px"
          />
        </template>
      </ClientOnly>
    </div>

    <CmsButton
      v-if="button"
      v-bind="button"
      class="mt-32 md:mt-24 !max-w-fit"
      :tracking="trackingData"
    />
  </Background>
</template>

<script setup lang="ts">
import { h, onMounted, ref, unref } from 'vue'

import type { Tracking } from '@backmarket/http-api/src/api-specs-content/models/tracking'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { tw } from '@backmarket/utils/string/tw'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { RevCounter } from '@ds/components/Counter'
import { RevCoupon } from '@ds/components/Coupon'
import { RevSkeleton } from '@ds/components/Skeleton'
import type { NodeRendererMap } from 'contentful-rich-text-vue-renderer'

import translations from '../../common/CMS.translations'
import Background from '../../shared-components/Background/Background.vue'
import { useLoadIcon } from '../../shared-components/DynamicIcon/helpers/useLoadIcon'
import CmsButton from '../Button/Button.vue'
import RichText from '../RichText/RichText.vue'
import { findTitleId } from '../RichText/RichTextVueParser.utils'

import type { CountdownBannerBlockProps } from './CountdownBanner.types'

const {
  coupon,
  title,
  subtitle,
  saleStartDate,
  button,
  mood,
  tracking,
  background,
} = defineProps<CountdownBannerBlockProps>()

const trackingData = ref<Tracking>()
const titleId = findTitleId(title)
const i18n = useI18n()

const desktopDateLabels = {
  weeks: i18n(translations.desktopWeeks),
  days: i18n(translations.desktopDays),
  hours: i18n(translations.desktopHours),
  minutes: i18n(translations.desktopMinutes),
  seconds: i18n(translations.desktopSeconds),
}

const mobileDateLabels = {
  weeks: i18n(translations.mobileWeeks),
  days: i18n(translations.mobileDays),
  hours: i18n(translations.mobileHours),
  minutes: i18n(translations.mobileMinutes),
  seconds: i18n(translations.mobileSeconds),
}

const fetchedIcon = await useLoadIcon(coupon?.icon?.icon)

onMounted(() => {
  trackingData.value = {
    ...tracking,
    title: documentToPlainTextString(title),
  }
})

// render the title as a h2 tag
const renderParagraphAsHeading: NodeRendererMap = {
  paragraph: (node, key, next) => {
    return h(
      'h2',
      { key, class: tw`empty:hidden`, id: titleId },
      next(node.content, key, next),
    )
  },
}
</script>
